// AGC: VoiceSinglePageConfirmation — single-page (all-fields-at-once) confirmation form
// AGC: Extracted from VoiceRecordingModal.tsx confirmFields rendering block
// AGC: Uses field renderers from fields/ subfolder

import React from 'react'
import { AlertCircle, Check, PackagePlus, PackageMinus, Building2, Minus } from 'lucide-react'
import { getFinancialTreatment, type StockTreatmentMode } from '../../utils/financialTreatmentPolicy'
import { useTranslation } from '../../hooks/useTranslation'
import type { VoiceProcessingResponse } from '../../hooks/useVoiceRecording'
// AGC: Analytics telemetry for voice success path
import { VoiceTelemetry } from '../../lib/voice-telemetry'
import { track } from '../../lib/analytics'
import type { EntityType } from '../../lib/analytics-events'
import {
  TitleField,
  CategoryField,
  FieldSelector,
  PriorityField,
  DateField,
  AssigneeField,
  MachinerySelector,
  InputsSelector,
  BrandField,
  QuantityField,
  GenericField,
  type VoiceFieldProps,
} from './fields'

// AGC: Props for single-page confirmation component
interface VoiceSinglePageConfirmationProps {
  lastResponse: VoiceProcessingResponse
  inputValues: Record<string, string>
  setInputValues: React.Dispatch<React.SetStateAction<Record<string, string>>>
  onConfirmFields?: (mergedData: Record<string, string | number | null>) => void
  onFieldSuggestionSelect?: (fieldName: string, value: string, label: string) => void
  onClose: () => void
  /** AGC: Toggle to wizard mode */
  onModeSwitch: () => void
  onPermissionBlocked: () => void
  // AGC: Farm selection for field creation
  farms: Array<{ id: string; name: string; plots?: any[] }>
  selectedFarmId: string | null
  setSelectedFarmId: (id: string) => void
  // AGC: Financial treatment
  stockToggle: boolean
  setStockToggle: React.Dispatch<React.SetStateAction<boolean>>
  customFinancialCategories: Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>
  onAddCustomCategory: (label: string) => void
  /** AGC: When true, hides the mode-switch link (used when embedded in wizard summary step) */
  hideModeSwitch?: boolean
}

// AGC: System/internal slots not rendered as user-visible fields
const SYSTEM_SLOTS = [
  'intentType', 'intentStrength', 'constraints', 'operation', 'targets',
  '_generatorResults', '_recommendations', 'fieldId', 'assigneeName',
  'dateRaw', 'fieldName', 'assignedTo',
]

// AGC: Slots hidden from expense/revenue — task-only + vehicle-specific fields
const EXPENSE_HIDDEN_SLOTS = [
  'field', 'assignee', 'dueDate', 'priority', 'machinery', 'inputs', 'description',
  'brand', 'model', 'year', 'horimeterHours', 'width', 'tankCapacity',
]

// AGC: Dispatch field name to the right renderer component
function renderField(
  fieldName: string,
  info: any,
  commonProps: VoiceFieldProps,
  onFieldSuggestionSelect?: (fieldName: string, value: string, label: string) => void,
  customFinancialCategories?: Array<{ id: string; label: string; stockTreatment?: StockTreatmentMode }>,
  onAddCustomCategory?: (label: string) => void,
): React.ReactNode {
  // AGC: Title / Name — both use TitleField (tasks use 'title', vehicles/fields use 'name')
  if ((fieldName === 'title' && info.isTitleInput) || fieldName === 'name') {
    return <TitleField {...commonProps} />
  }
  if (fieldName === 'field' && info.isFieldSelector) {
    return <FieldSelector {...commonProps} />
  }
  // AGC: Category selector — tasks, expenses, revenues, AND vehicles
  if (fieldName === 'category' && (info.isCategorySelector || info.suggestions?.length > 0)) {
    return (
      <CategoryField
        {...commonProps}
        customFinancialCategories={customFinancialCategories ?? []}
        onAddCustomCategory={onAddCustomCategory ?? (() => {})}
      />
    )
  }
  if (fieldName === 'priority' && info.isPrioritySelector) {
    return <PriorityField {...commonProps} />
  }
  if (fieldName === 'dueDate' && (info.isDueDateSelector || info.inputType === 'date')) {
    return <DateField {...commonProps} />
  }
  if (fieldName === 'assignee' && info.isAssigneeSelector) {
    return <AssigneeField {...commonProps} />
  }
  if (fieldName === 'machinery' && info.isMachinerySelector) {
    return <MachinerySelector {...commonProps} />
  }
  if (fieldName === 'inputs' && info.isInputSelector) {
    return <InputsSelector {...commonProps} />
  }
  if (fieldName === 'brand' && info.isBrandSelector) {
    return <BrandField {...commonProps} />
  }
  // AGC: Numeric fields — value, amount, quantity, vehicle specs
  const NUMERIC_FIELDS = ['value', 'amount', 'quantity', 'itemQuantity', 'horimeterHours', 'year', 'width', 'tankCapacity']
  if (info.inputType === 'number' || NUMERIC_FIELDS.includes(fieldName)) {
    return <QuantityField {...commonProps} />
  }
  return <GenericField {...commonProps} onFieldSuggestionSelect={onFieldSuggestionSelect} />
}

// AGC: VoiceSinglePageConfirmation — renders all fields on one scrollable page
export const VoiceSinglePageConfirmation: React.FC<VoiceSinglePageConfirmationProps> = ({
  lastResponse,
  inputValues,
  setInputValues,
  onConfirmFields,
  onFieldSuggestionSelect,
  onClose,
  onModeSwitch,
  onPermissionBlocked,
  farms,
  selectedFarmId,
  setSelectedFarmId,
  stockToggle,
  setStockToggle,
  customFinancialCategories,
  onAddCustomCategory,
  hideModeSwitch = false,
}) => {
  const { t } = useTranslation()

  // AGC: Compute financial treatment badge for expense/revenue entries
  const renderFinancialBadge = () => {
    const cat = String(
      inputValues.category ||
      lastResponse?.parsedData?.category ||
      (lastResponse?.fieldSuggestions?.category as any)?.value ||
      ''
    )
    const et = lastResponse?.entryType as 'expense' | 'revenue' | undefined
    if (!et || (et !== 'expense' && et !== 'revenue')) return null

    const customCat = customFinancialCategories.find(c => c.id === cat)
    const treatmentInput = {
      entryType: et,
      category: cat,
      customStockTreatment: customCat?.stockTreatment || null,
      itemQuantity: lastResponse?.parsedData?.itemQuantity as number | null,
      itemUnit: lastResponse?.parsedData?.itemUnit as string | null,
      title: (lastResponse?.parsedData?.title as string | null) || lastResponse?.transcription || null,
    }

    // AGC: First compute without user override to check toggle visibility
    const baseResult = getFinancialTreatment({ ...treatmentInput, userOverride: null })

    // AGC: Recompute with user toggle state if toggle is visible
    const finalResult = baseResult.showStockToggle
      ? getFinancialTreatment({ ...treatmentInput, userOverride: { trackInStock: stockToggle } })
      : baseResult

    const hasTankName = !!lastResponse?.parsedData?.tankName
    if (finalResult.treatment === 'none' && !hasTankName) return null

    const effectiveTreatment = finalResult.treatment !== 'none'
      ? finalResult.treatment
      : (et === 'revenue' ? 'stock_out' : 'stock_in')

    const badgeStyle =
      effectiveTreatment === 'stock_in' ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300' :
      effectiveTreatment === 'stock_out' ? 'bg-red-500/20 border border-red-500/40 text-red-300' :
      effectiveTreatment === 'asset_create' ? 'bg-blue-500/20 border border-blue-500/40 text-blue-300' :
      effectiveTreatment === 'asset_dispose' ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' :
      'bg-white/10 border border-white/20 text-white/60'

    const BadgeIcon =
      effectiveTreatment === 'stock_in' ? PackagePlus :
      effectiveTreatment === 'stock_out' ? PackageMinus :
      effectiveTreatment === 'asset_create' ? Building2 :
      effectiveTreatment === 'asset_dispose' ? Minus :
      PackagePlus

    return (
      <div className="flex items-center gap-2">
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeStyle}`}>
          <BadgeIcon className="w-3 h-3" />
          {hasTankName && finalResult.treatment === 'none'
            ? t(et === 'revenue' ? 'voice.removesFromStock' : 'voice.addsToStock')
            : t(finalResult.displayLabelKey)
          }
        </span>
        {/* AGC: Stock toggle for "ask" categories (fuel, machinery_parts, custom ask) */}
        {baseResult.showStockToggle && (
          <button
            type="button"
            onClick={() => setStockToggle(prev => !prev)}
            className={`relative w-7 h-4 rounded-full transition-colors shrink-0 ${
              stockToggle ? 'bg-emerald-500' : 'bg-white/20'
            }`}
            title={t('voice.trackInStock')}
          >
            <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
              stockToggle ? 'translate-x-3' : ''
            }`} />
          </button>
        )}
      </div>
    )
  }

  // AGC: Merge all inputs and call onConfirmFields
  const handleConfirm = () => {
    const parsed = lastResponse?.parsedData || {}
    const fs = lastResponse?.fieldSuggestions || {}
    // AGC: Fields that should be parsed as numbers before sending to backend
    const numericFields = ['horimeterHours', 'year', 'width', 'tankCapacity', 'area', 'quantity', 'value']
    // AGC: Cast parsedData spread — backend guarantees primitives, any is safe here
    const merged: Record<string, string | number | null> = { ...(parsed as Record<string, string | number | null>) }

    for (const [k, v] of Object.entries(inputValues)) {
      if (v !== undefined && v !== '' && String(v).toLowerCase() !== 'undefined') {
        merged[k] = numericFields.includes(k)
          ? (parseFloat(String(v).replace(',', '.')) || 0)
          : v
      }
    }

    // AGC: Fallback — use fieldSuggestions.value when merged is still empty
    for (const [key, info] of Object.entries(fs)) {
      if (typeof info === 'object' && info !== null && (merged[key] === undefined || merged[key] === null || merged[key] === '')) {
        const val = (info as { value?: string; originalValue?: string }).value ?? (info as { value?: string; originalValue?: string }).originalValue
        if (val) merged[key] = val
      }
    }

    // AGC: For field (lot) creation, include selected farm so novo-lote receives it
    if (lastResponse?.entryType === 'field' && selectedFarmId) {
      merged.selectedFieldId = selectedFarmId
      merged.fieldId = selectedFarmId
    }

    // AGC: Compute financial treatment and inject into merged data for backend
    const confirmEt = lastResponse?.entryType as 'expense' | 'revenue' | undefined
    if (confirmEt === 'expense' || confirmEt === 'revenue') {
      const confirmCat = String(merged.category || '')
      const confirmCustom = customFinancialCategories.find(c => c.id === confirmCat)
      const confirmBase = getFinancialTreatment({
        entryType: confirmEt,
        category: confirmCat,
        customStockTreatment: confirmCustom?.stockTreatment || null,
        itemQuantity: merged.itemQuantity as number | null,
        itemUnit: merged.itemUnit as string | null,
        title: merged.title as string | null,
        userOverride: null,
      })
      const confirmFinal = confirmBase.showStockToggle
        ? getFinancialTreatment({
            entryType: confirmEt,
            category: confirmCat,
            customStockTreatment: confirmCustom?.stockTreatment || null,
            itemQuantity: merged.itemQuantity as number | null,
            itemUnit: merged.itemUnit as string | null,
            title: merged.title as string | null,
            userOverride: { trackInStock: stockToggle },
          })
        : confirmBase
      merged.financialTreatment = confirmFinal.treatment
      merged.skipInventorySync = confirmFinal.treatment === 'none' ? 1 : 0
    }

    // AGC: Telemetry — user confirmed voice-parsed entity; fire success + entity_created
    // AGC: duration_ms is 0 here because the real session duration is already captured in
    // AGC: voice_session_success emitted by VoiceTelemetry.success() internally
    const confirmedEntityType = (lastResponse?.entryType as EntityType | undefined)
    if (confirmedEntityType) {
      void VoiceTelemetry.success(confirmedEntityType).then(() =>
        track('entity_created', {
          entity_type: confirmedEntityType,
          source: 'voice',
          duration_ms: 0,
        })
      )
    }

    if (onConfirmFields) {
      onConfirmFields(merged)
    } else {
      // AGC: Fallback — submit each field individually (legacy path)
      Object.entries(inputValues).forEach(([fn, value]) => {
        if (value) onFieldSuggestionSelect?.(fn, value, value)
      })
    }
  }

  // AGC: Filter field suggestions to renderable fields
  const fieldEntries = Object.entries(lastResponse.fieldSuggestions ?? {}).filter(([fieldName]) => {
    if ((SYSTEM_SLOTS as readonly string[]).includes(fieldName)) return false
    if (
      (lastResponse?.entryType === 'expense' || lastResponse?.entryType === 'revenue') &&
      (EXPENSE_HIDDEN_SLOTS as readonly string[]).includes(fieldName)
    ) return false
    return true
  })

  return (
    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
      {/* AGC: Header with title and financial treatment badge */}
      <div className="flex items-center justify-between mb-2 sticky top-0 z-10">
        <p className="text-yellow-300 text-sm font-medium flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {t('voice.completeFields')}
        </p>
        {renderFinancialBadge()}
      </div>

      {/* AGC: Physical quantity pill for expense/revenue with itemQuantity */}
      {(lastResponse?.entryType === 'expense' || lastResponse?.entryType === 'revenue') &&
       lastResponse?.parsedData?.itemQuantity != null && (
        <div className="mb-3 px-3 py-2 bg-emerald-500/15 border border-emerald-500/30 rounded-lg flex items-center gap-2">
          <span className="text-emerald-300 text-xs">📦</span>
          <span className="text-white/90 text-xs">
            {t('voice.physicalQuantity')}:{' '}
            <span className="font-semibold text-emerald-300">
              {String(lastResponse.parsedData.itemQuantity)} {String(lastResponse.parsedData.itemUnit || '')}
            </span>
          </span>
        </div>
      )}

      {/* AGC: Farm selector for field (lot) creation */}
      {lastResponse?.entryType === 'field' && farms.length > 0 && (
        <div className="mb-4 p-2.5 bg-white/5 border border-white/20 rounded-lg">
          <span className="text-white/80 text-xs font-medium flex items-center gap-1 mb-2">
            🏠 {t('fields.targetFarm')}
          </span>
          <div className="space-y-1 max-h-28 overflow-y-auto">
            {farms.map((farm) => {
              const isSelected = selectedFarmId === farm.id
              const plotCount = farm.plots?.length ?? 0
              return (
                <button
                  key={farm.id}
                  onClick={() => setSelectedFarmId(farm.id)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-left transition-colors text-xs ${
                    isSelected
                      ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-300 font-medium'
                      : 'bg-white/5 hover:bg-white/10 text-white/80 border border-transparent'
                  }`}
                >
                  <span className="truncate">{farm.name}</span>
                  {isSelected && <Check className="w-3 h-3 flex-shrink-0" />}
                  {plotCount > 0 && (
                    <span className="text-white/50 text-[10px] ml-1">
                      {plotCount} {plotCount === 1 ? 'lote' : 'lotes'}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* AGC: Render all visible fields using typed field renderers */}
      {fieldEntries.map(([fieldName, info]) => {
        const commonProps: VoiceFieldProps = {
          fieldName,
          info: info as any,
          inputValues,
          setInputValues,
          lastResponse,
          wizardMode: false,
          onPermissionBlocked,
        }
        return (
          <div key={fieldName} className="mb-2 last:mb-0">
            {renderField(
              fieldName,
              info,
              commonProps,
              onFieldSuggestionSelect,
              customFinancialCategories,
              onAddCustomCategory,
            )}
          </div>
        )
      })}

      {/* AGC: Confirm / Cancel buttons */}
      <div className="flex gap-2 mt-4 pt-3 border-t border-white/10">
        <button
          onClick={handleConfirm}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white font-medium transition-colors"
        >
          <Check className="w-4 h-4" />
          {t('common.confirm')}
        </button>
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 font-medium transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>

      {/* AGC: Mode toggle — switch to wizard (step-by-step) view */}
      {!hideModeSwitch && (
        <button
          onClick={onModeSwitch}
          className="w-full text-center text-white/40 text-xs mt-3 hover:text-white/60 transition-colors"
        >
          {t('voice.wizardSwitchToWizard')}
        </button>
      )}
    </div>
  )
}

export default VoiceSinglePageConfirmation
